import * as React from 'react'
import styled from 'styled-components'
import { extent } from 'd3-array'
import { BREAKPOINTS } from '@ta-interaktiv/react-components'
import { type EChartOption } from 'echarts'

const Client = React.lazy(() => import('./internal/Client'))

interface Item {
  Time: string
  Estimate: string
  Share_counted: string

  label: string
  label_fr: string

  result: string
  result_fr: string
}

interface Props {
  csvData: Item[]
  lineColors: {
    approved: string
    declined: string
    tie: string
  }
  projectionLanguage: string
}

function BallotProjectionSparkline(props: Props) {
  const lineData: number[] = props.csvData.map((projection) => parseFloat(projection.Estimate))
  const [minY = 0, maxY = 0] = extent(lineData)

  const getLastIndex = () => {
    return lineData.length - 1
  }

  const getFirstValue = () => {
    return lineData[0]
  }

  const getLastValue = () => {
    return lineData[lineData.length - 1]
  }

  const getFirstTime = () => {
    return props.csvData[0].Time.substring(0, props.csvData[0].Time.length - 3)
  }

  const getLastTime = () => {
    return props.csvData[getLastIndex()].Time.substring(0, props.csvData[getLastIndex()].Time.length - 3)
  }

  const isAllNegative = () => {
    return !lineData.some((v) => v > 50) //any Values below 50?
  }

  // Chart Options
  // https://ecomfe.github.io/echarts-doc/public/en/option.html#title
  const getOption = (): EChartOption<any> => ({
    title: {
      text: '',
      subtext: '',
    },
    tooltip: {
      position: 'left',
      show: true,
      axisPointer: {
        type: 'none',
      },
      formatter: function (params: any) {
        // console.log('#### params ####', params)
        const currIndex = params[0].dataIndex
        const currResult =
          props.projectionLanguage === 'fr' ? props.csvData[currIndex].result_fr : props.csvData[currIndex].result
        const currLabel =
          props.projectionLanguage === 'fr' ? props.csvData[currIndex].label_fr : props.csvData[currIndex].label
        let currTime = params[0].name.substring(0, params[0].name.length - 3)
        if (props.projectionLanguage === 'fr') {
          currTime = currTime.replace(':', 'h')
        }
        const currCounted = Math.round(+props.csvData[currIndex].Share_counted * 100) + '%'
        const title = '<div class="tip-title">' + currTime + '<br />' + currLabel + '</div>'
        const communitiesText = props.projectionLanguage === 'fr' ? ' des communes' : ' der Gemeinden'
        let tooltipContent = title + '<hr class="under" />'
        // console.log('estimate:', parseFloat(params[0].data))
        if (parseFloat(params[0].data) > -0.00000000001) {
          let prognoseValue = parseFloat(params[0].data)
          // console.log('prognoseValue:', prognoseValue)
          let prognoseClass = 'tie'
          if (prognoseValue < 50) {
            prognoseValue = 100 - prognoseValue
            prognoseClass = 'declined'
          } else if (prognoseValue > 50) {
            prognoseClass = 'approved'
          } else {
            prognoseClass = 'tie'
          }
          prognoseValue = Math.round(prognoseValue * 10) / 10
          tooltipContent += '<div class="row ' + prognoseClass + '">'
          tooltipContent += '<span class="prognose-text">' + currResult + ':</span> '
          tooltipContent +=
            '<span>' +
            prognoseValue.toString().split('.').join(',') +
            '%</span></div>' +
            '<div class="tip-bottom">' +
            currCounted +
            communitiesText +
            '</div>'
        }
        // callback(console.log('scrubbed to: ', params[0].dataIndex))
        return tooltipContent
      },
      textStyle: {
        color: '#333',
      },
      backgroundColor: '#f3f3f3',
      trigger: 'axis',
    },
    grid: [
      {
        top: '22%',
        left: 25,
        right: 60,
        bottom: 10,
        containLabel: true,
      },
    ],
    xAxis: {
      show: false,
      type: 'category',
      boundaryGap: false,
      data: [...new Set(props.csvData.map((projection) => projection.Time))],
    },
    yAxis: {
      show: false,
      type: 'value',
      inverse: isAllNegative(), //inverte YAxis when to estimate is all negative (< 50)
      min: minY,
      max: maxY,
    },
    series: [
      {
        name: 'Prognose',
        type: 'line',
        zlevel: 2,
        showSymbol: false,
        symbolSize: 3.5,
        connectNulls: true,
        color: props.lineColors.declined, // this has to be defined in parent component
        data: lineData,
        markPoint: {
          data: [
            {
              name: 'Startpunkt',
              coord: [0, getFirstValue()],
              itemStyle: {
                color: getFirstValue() > 50 ? props.lineColors.approved : props.lineColors.declined,
              },
              label: {
                formatter: function (param: any) {
                  let cleanValue = parseFloat(param.data.coord[1])
                  if (cleanValue < 50) {
                    cleanValue = 100 - cleanValue
                  }
                  cleanValue = Math.round(cleanValue * 10) / 10
                  const cleanTime =
                    props.projectionLanguage === 'fr' ? getFirstTime().replace(':', 'h') : getFirstTime()
                  return ['{b|' + cleanTime + '}', '{c|' + `${cleanValue}%`.split('.').join(',') + '}'].join('\n')
                },
                rich: {
                  b: {
                    fontSize: 14,
                    lineHeight: 21,
                    color: getFirstValue() > 50 ? props.lineColors.approved : props.lineColors.declined,
                    padding: [3, 0, 0, 0],
                    backgroundColor: 'transparent',
                  },
                  c: {
                    fontSize: 14,
                    lineHeight: 21,
                    fontWeight: 'bold',
                    color: getFirstValue() > 50 ? props.lineColors.approved : props.lineColors.declined,
                    padding: [-3, 0, 0, 0],
                    backgroundColor: 'transparent',
                  },
                },
                position: 'left',
              },
            },
            {
              name: 'Endpunkt',
              coord: [getLastIndex(), getLastValue()],
              itemStyle: {
                color: getLastValue() > 50 ? props.lineColors.approved : props.lineColors.declined,
              },
              label: {
                formatter: function (param: any) {
                  let cleanValue = parseFloat(param.data.coord[1])
                  if (cleanValue < 50) {
                    cleanValue = 100 - cleanValue
                  }
                  cleanValue = Math.round(cleanValue * 10) / 10
                  const cleanTime = props.projectionLanguage === 'fr' ? getLastTime().replace(':', 'h') : getLastTime()
                  return ['{b|' + cleanTime + '}', '{c|' + `${cleanValue}%`.toString().split('.').join(',') + '}'].join(
                    '\n',
                  )
                },
                rich: {
                  b: {
                    fontSize: 14,
                    lineHeight: 21,
                    color: getLastValue() > 50 ? props.lineColors.approved : props.lineColors.declined,
                    padding: [3, 0, 0, 0],
                    backgroundColor: 'transparent',
                  },
                  c: {
                    fontSize: 14,
                    lineHeight: 21,
                    fontWeight: 'bold',
                    color: getLastValue() > 50 ? props.lineColors.approved : props.lineColors.declined,
                    padding: [-3, 0, 0, 0],
                    backgroundColor: 'transparent',
                  },
                },
                position: 'right',
              },
            },
          ],
          symbol: 'circle',
          symbolSize: 8,
        },
      },
    ],
    visualMap: [
      {
        show: false,
        zlevel: -1,
        dimension: 1,
        pieces: [
          {
            min: 0,
            max: 50,
            color: props.lineColors.declined,
          },
          {
            min: 50,
            max: 100,
            color: props.lineColors.approved,
          },
        ],
      },
    ],
  })

  return (
    <Root>
      <React.Suspense fallback={null}>
        <Client option={getOption()} />
      </React.Suspense>
    </Root>
  )
}

const Root = styled.div`
  height: 70px;

  @media screen and (min-width: ${BREAKPOINTS.tablet}px) {
    height: 100px;
  }

  -webkit-font-smoothing: antialiased;

  .tip-title,
  .tip-bottom {
    text-align: center;
    font-size: 1em;
    line-height: 1.2em;
    font-weight: 400;
    color: var(--brandblue-main);
  }
  .row {
    font-size: 1em;
    font-weight: 700;
  }
  hr.under {
    margin: 0.3em 0;
    border: 0;
    height: 0;
    border-top: 1px solid #d4d4d4;
    border-bottom: 0;
  }

  .row {
    text-align: center;
  }
  .approved {
    color: var(--blue-main);
  }
  .declined {
    color: var(--red-main);
  }
  .tie {
    color: var(--gray-color);
  }
`

export default BallotProjectionSparkline
